import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import { updateQueriesData } from 'services/update_queries_data';
import { useDangerousMutation } from 'hooks/api/useDangerousMutation';

import { useAPI } from 'hooks/api/useAPI';
import { useAccount } from 'hooks/api/useAccount';

import APIError from 'types/api_error';
import { Account } from 'types/account';
import { ApplicationRole, isApplicationRole } from 'types/account_role';

type DeleteApplicationRolesVariables = {
  account: Account;
  deletedRoles: ApplicationRole[];
};

/** Remove application roles from a member */
export function useDeleteApplicationRoles({
  onMutate,
  onSettled,
  onError,
  ...options
}: UseMutationOptions<void, APIError, DeleteApplicationRolesVariables> = {}) {
  const queryClient = useQueryClient();
  const { applicationID, client } = useAPI();
  const { data: currentAccount } = useAccount();

  return useDangerousMutation(
    'Are you sure you want to remove this role from the member?',
    async ({ account, deletedRoles }) => {
      if (!account.id) {
        return Promise.reject(new Error('Missing account id.'));
      }

      return client.delete(
        `/internal/apps/${applicationID}/members/${account.id}/roles`,
        { params: { roles: deletedRoles } }
      );
    },
    {
      ...options,
      action: 'remove',
      entity: 'role',
      onMutate({ account, deletedRoles }) {
        onMutate?.({ account, deletedRoles });

        if (!account.id) {
          return;
        }

        if (currentAccount && currentAccount.id === account.id) {
          queryClient.cancelQueries(['account']);
        }

        queryClient.cancelQueries(['members']);
        queryClient.cancelQueries(['member', account.id, applicationID]);

        const newAccount = { ...account };

        newAccount.roles = newAccount.roles.filter(({ name }) => {
          return isApplicationRole(name) ? !deletedRoles.includes(name) : true;
        });

        return updateQueriesData({
          queryClient,
          listQueryKey: ['members'],
          singleQueryKey: ['member'],
          ids: [account.id],
          updateData: newAccount,
        });
      },
      onError(error, variables, context) {
        onError?.(error, variables, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      onSettled(data, error, { account, deletedRoles }, context) {
        onSettled?.(data, error, { account, deletedRoles }, context);

        if (currentAccount && currentAccount.id === account.id) {
          queryClient.invalidateQueries(['account']);
        }

        queryClient.invalidateQueries(['member', account.id, applicationID]);
        queryClient.invalidateQueries(['members']);
      },
    }
  );
}
